import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import Modal from '../../common/Modal';
import PhoneVerificationModal from '../../common/PhoneVerificationModal';
import EmailVerificationModal from '../../common/EmailVerificationModal';
import ProfileSection from '../../../features/profile/sections/ProfileSection';

/**
 * Account tab content for AccountModal. Profile/settings for the current user.
 */
function AccountTab({ user, setUser }) {
  const { darkMode } = useTheme();
  const fileInputRef = useRef(null);
  const [userRole, setUserRole] = useState('member');
  const initializationDone = useRef(false);

  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');

  useEffect(() => {
    if (!user?.id || initializationDone.current) return;
    initializationDone.current = true;

    const savedRole = localStorage.getItem('userRole');
    if (savedRole) setUserRole(savedRole);

    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/organization/my-role`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const role = data.role || 'member';
        setUserRole(role);
        localStorage.setItem('userRole', role);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole('member');
      }
    };
    fetchUserRole();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) initializationDone.current = false;
  }, [user?.id]);

  const toastStyle = {
    style: {
      background: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
      border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    },
    success: {
      style: {
        background: darkMode ? '#0c4a2d' : '#ecfdf5',
        color: darkMode ? '#fff' : '#065f46',
        border: `1px solid ${darkMode ? '#065f46' : '#d1fae5'}`,
      },
    },
    error: {
      style: {
        background: darkMode ? '#4c1d24' : '#fef2f2',
        color: darkMode ? '#fff' : '#b91c1c',
        border: `1px solid ${darkMode ? '#b91c1c' : '#fee2e2'}`,
      },
    },
    loading: {
      style: {
        background: darkMode ? '#1e3a8a' : '#eff6ff',
        color: darkMode ? '#fff' : '#1e40af',
        border: `1px solid ${darkMode ? '#1e40af' : '#dbeafe'}`,
      },
    },
  };

  const handlePhoneVerificationConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update-phone`,
        { phoneNumber: pendingPhoneNumber },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const updatedUser = { ...user, phoneNumber: pendingPhoneNumber };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setShowPhoneVerificationModal(false);
      setPendingPhoneNumber('');
      toast.success('Phone number updated successfully', toastStyle.success);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update phone number. Please try again.';
      toast.error(errorMessage, toastStyle.error);
    }
  }, [pendingPhoneNumber, user, setUser, toastStyle]);

  const handleEmailVerificationConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update`,
        { email: pendingEmail },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const updatedUser = { ...user, email: pendingEmail };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setShowEmailVerificationModal(false);
      setPendingEmail('');
      toast.success('Email address updated successfully', toastStyle.success);
    } catch (error) {
      toast.error('Failed to update email address. Please try again.', toastStyle.error);
    }
  }, [pendingEmail, user, setUser, toastStyle]);

  if (!user) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex items-center justify-center">
        <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
          Loading profile...
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        <ProfileSection
          user={user}
          fileInputRef={fileInputRef}
          setUser={setUser}
          userRole={userRole}
          showPhoneVerificationModal={showPhoneVerificationModal}
          setShowPhoneVerificationModal={setShowPhoneVerificationModal}
          pendingPhoneNumber={pendingPhoneNumber}
          setPendingPhoneNumber={setPendingPhoneNumber}
          onPhoneVerificationConfirm={handlePhoneVerificationConfirm}
          showEmailVerificationModal={showEmailVerificationModal}
          setShowEmailVerificationModal={setShowEmailVerificationModal}
          pendingEmail={pendingEmail}
          setPendingEmail={setPendingEmail}
          onEmailVerificationConfirm={handleEmailVerificationConfirm}
          variant="embedded"
        />
      </div>

      <PhoneVerificationModal
        isOpen={showPhoneVerificationModal}
        onClose={() => {
          setShowPhoneVerificationModal(false);
          setPendingPhoneNumber('');
        }}
        onConfirm={handlePhoneVerificationConfirm}
        phoneNumber={pendingPhoneNumber}
        title="Verify New Phone Number"
      />

      <EmailVerificationModal
        isOpen={showEmailVerificationModal}
        onClose={() => {
          setShowEmailVerificationModal(false);
          setPendingEmail('');
        }}
        onConfirm={handleEmailVerificationConfirm}
        email={pendingEmail}
        title="Verify New Email Address"
      />
    </>
  );
}

export default AccountTab;
